import type { StaffAnalyticsAccess } from "@lafayette/schemas";

import { AsyncState } from "../../ui/async-state";
import { Button } from "../../ui/button";
import { Notice } from "../../ui/notice";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "../../ui/sheet";
import { durationText } from "../format";
import { type CustomerView, isRefusal } from "../queries";
import { visitSpan } from "./events-model";
import { useVisitSteps } from "./events-queries";
import { VisitTimeline } from "./VisitTimeline";

// One visit of the phone app, step by step, in a sheet (CHT-2030). It opens from the address (`?tab=events&visit=<visit id>`), which is what the
// "Open visit" link of Explore builds, and from nowhere else in this tab. The steps are the closed sentences of their events, oldest first. A visit
// that is not this customer's gets the same refusal words as every other refusal. An opted-out customer gets no step.

type Steps = ReturnType<typeof useVisitSteps>;
type VisitAnswer = NonNullable<Steps["data"]>;

function VisitFailed({ steps }: Readonly<{ steps: Steps }>) {
  const refused = isRefusal(steps.error);
  return (
    <AsyncState
      kind="error"
      title={refused ? "This visit is not available to you" : "The visit could not be loaded"}
      message={
        refused
          ? "You may open only the visits of the customers you are allowed to see."
          : "Nothing was changed. Try again in a moment."
      }
      {...(refused ? {} : { onRetry: () => steps.refetch() })}
    />
  );
}

function VisitSteps({ answer, tz }: Readonly<{ answer: VisitAnswer; tz: string }>) {
  if (answer.availability === "VENDOR_UNAVAILABLE") {
    return (
      <Notice tone="warning" role="status">
        The steps of this visit could not be read right now.
      </Notice>
    );
  }
  return <VisitTimeline steps={answer.steps} openUuid={null} tz={tz} />;
}

function VisitDetail({
  answer,
  access,
  onShowEvents,
}: Readonly<{ answer: VisitAnswer; access: StaffAnalyticsAccess; onShowEvents: () => void }>) {
  const { tz, to: now } = access.window;
  const { visit } = answer;
  if (visit === null) return null;
  return (
    <div className="grid gap-3">
      <p className="m-0 text-sm font-semibold">
        {visitSpan(visit.startedAt, visit.endedAt, tz, now)}
      </p>
      <p className="m-0 text-xs text-muted-foreground">
        {visit.platform === "android" ? "Android" : "iOS"} · {durationText(visit.durationSeconds)} ·{" "}
        {visit.screenCount} {visit.screenCount === 1 ? "screen" : "screens"}
      </p>
      <VisitSteps answer={answer} tz={tz} />
      {answer.stepsTruncated ? (
        <p className="m-0 text-xs text-muted-foreground">Only the first 100 steps are shown.</p>
      ) : null}
      <Button type="button" variant="outline" className="rounded-xl" onClick={onShowEvents}>
        Show only the events of this visit
      </Button>
    </div>
  );
}

function VisitBody({
  steps,
  access,
  onShowEvents,
}: Readonly<{ steps: Steps; access: StaffAnalyticsAccess; onShowEvents: () => void }>) {
  if (steps.isError) return <VisitFailed steps={steps} />;
  const answer = steps.data;
  if (answer === undefined) {
    return <AsyncState kind="loading" title="Loading the visit" message="One moment." />;
  }
  if (
    answer.availability === "OPTED_OUT" ||
    answer.availability === "ANALYTICS_OFF" ||
    answer.visit === null
  ) {
    return (
      <Notice tone="info" role="note">
        No visit is shown: app events are not shown for this customer.
      </Notice>
    );
  }
  return <VisitDetail answer={answer} access={access} onShowEvents={onShowEvents} />;
}

export function VisitSheet({
  view,
  customerId,
  visitId,
  access,
  onClose,
  onShowEvents,
}: Readonly<{
  view: CustomerView;
  customerId: string;
  visitId: string;
  access: StaffAnalyticsAccess;
  onClose: () => void;
  onShowEvents: () => void;
}>) {
  const steps = useVisitSteps(view, customerId, visitId);
  return (
    <Sheet open onOpenChange={(open) => (open ? undefined : onClose())}>
      <SheetContent
        side="right"
        closeLabel="Close the visit"
        className="w-[min(26rem,100vw)] overflow-y-auto bg-card p-5 text-foreground"
      >
        <SheetTitle className="font-serif text-xl font-medium">This visit, step by step</SheetTitle>
        <SheetDescription className="sr-only">
          One visit of the phone app, with its steps in order.
        </SheetDescription>
        <div className="mt-4">
          <VisitBody steps={steps} access={access} onShowEvents={onShowEvents} />
        </div>
      </SheetContent>
    </Sheet>
  );
}
